// ============================================================
//  Reiter „Plan" — was setzen wir um, und wer bekommt welche Rolle?
//  Alle Zahlen aus planResult (Zielzustand, R4). Kein Fließtext: Kacheln, Tabellen, InfoTips.
// ============================================================
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Download, FileText, Plus, Trash2, Ticket } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip, Aufklapp } from "../components/Erklaerung";
import { Badge, Card, KpiCard } from "../components/ui";
import { RoleName } from "../components/RoleName";
import { useApp } from "../../store/AppStore";
import { planStore, usePlanItems, TRACE_SCOPE_LABEL, type PlanItem, type TraceScope } from "../../store/plan";
import { planResult, type MissingCombo, type PlanHeldRow, type PlanItemResult, type PlanPersonRow } from "../../domain/plan";
import { CLASS_LABEL, formatEuro, formatFue, formatFue1, formatNum } from "../../domain/rules";
import type { Lever, TraceState } from "../../domain/types";
import { landscapeById } from "../../data/landscapes";
import { TRACE_META, traceWindowLabel } from "../../data/traceMeta";
import { downloadText, orderedItems, planCsv, planPdf, TRACE_LABEL } from "../../lib/planExport";

const SCOPES: TraceScope[] = ["nie", "nie+genutzt", "alle"];
const EFFORT_TONE: Record<string, "green" | "amber" | "red"> = { niedrig: "green", mittel: "amber", hoch: "red" };
const TRACE_COLOR: Record<TraceState, string> = { "nie-genutzt": "var(--color-amp-green)", genutzt: "var(--color-amp-amber)", "kein-trace": "var(--color-amp-gray)" };
const TRACES: TraceState[] = ["nie-genutzt", "genutzt", "kein-trace"];
const PAGE = 200;

export function Plan() {
  const { ds, ratio, price, contracted } = useApp();
  const items = usePlanItems();
  const r = useMemo(() => planResult(ds, items, ratio, price), [ds, items, ratio, price, contracted]);
  const ordered = useMemo(() => orderedItems(items, r), [items, r]);
  const leverById = useMemo(() => new Map(ds.levers.map((l) => [l.id, l])), [ds]);
  // Positionen aus einem früheren Datenstand (Maßnahme existiert nicht mehr) zählen nicht mit.
  const known = useMemo(() => items.filter((i) => leverById.has(i.leverId)), [items, leverById]);
  const [confirmClear, setConfirmClear] = useState(false);
  const puffer = r.restLuecke <= 0;

  const kopf = (
    <div className="flex flex-wrap items-center gap-2">
      <Link to="/optimierung" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}><Plus size={14} /> Maßnahmen wählen</Link>
      {known.length > 0 && (
        <>
          <button type="button" onClick={() => planPdf(ds, items, r)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}><FileText size={14} /> Plan als PDF</button>
          <button type="button" onClick={() => downloadText(`Umsetzungsplan_Personen_${new Date().toISOString().slice(0, 10)}.csv`, planCsv(ds, items, r))} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-dim)" }}><Download size={14} /> Personen als CSV</button>
          {confirmClear ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm" style={{ borderWidth: 1, borderColor: "var(--color-amp-red)" }}>
              <span className="text-dim text-xs">{formatNum(known.length)} Positionen wirklich leeren?</span>
              <button type="button" onClick={() => { planStore.clear(); setConfirmClear(false); }} className="rounded-md px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: "var(--color-amp-red)", color: "#fff" }}>Ja, leeren</button>
              <button type="button" onClick={() => setConfirmClear(false)} className="text-dim rounded-md px-2 py-0.5 text-xs font-medium">Abbrechen</button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmClear(true)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--color-amp-red)" }}><Trash2 size={14} /> Plan leeren</button>
          )}
        </>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader route="/plan" right={kopf} />

      {known.length === 0 ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-4 py-2">
            <div className="flex items-center gap-3">
              <ClipboardList size={28} className="text-faint" />
              <div>
                <div className="text-strong font-semibold">Der Plan ist leer</div>
                <div className="text-dim text-sm">Maßnahmen im Reiter Optimierung mit „in den Plan" übernehmen — der Plan bleibt über Reload und Tab-Wechsel erhalten.</div>
              </div>
            </div>
            <Link to="/optimierung" className="rounded-lg px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}>Maßnahmen wählen</Link>
          </div>
        </Card>
      ) : (
        <>
          {/* Kopf-Kacheln */}
          <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
            <KpiCard label="Positionen" value={formatNum(r.items)} sub={`${formatNum(known.filter((i) => i.ticket).length)} mit Ticket`} />
            <KpiCard label="Personen" value={formatNum(r.persons)} sub="stufen sich ab · jede einmal" />
            <KpiCard label={<span className="inline-flex items-center gap-1">Zielzustand<InfoTip>Reduktion im Zielzustand: für jede Person werden genau die Rollen der Positionen entfernt, bei denen sie im Umfang liegt; Klasse danach = Maximum über alle verbleibenden Rollen aller Systeme und Mandanten (R2), Untergrenze Self-Service. Zwei Positionen auf dieselbe Person zählen einmal (R4) — die Summe der Einzelwirkungen wäre zu hoch.</InfoTip></span>} value={`−${formatFue(r.reduction)}`} tone="green" />
            <KpiCard label="Einsparung" value={`−${formatEuro(r.euroSaved)}`} sub={`/ Jahr · ${formatEuro(price)} je FUE`} tone="green" />
            <KpiCard label="Bedarf nach Plan" value={formatFue(r.afterPlan)} sub={<span className="tnum"><span style={{ color: puffer ? "var(--color-amp-green)" : "var(--color-amp-red)" }}>{puffer ? "Puffer" : "Lücke"} {formatFue(Math.abs(r.restLuecke))}</span> · Vertrag {formatFue(r.contracted)}</span>} tone={puffer ? "green" : "red"} />
          </div>

          {/* Fehlende Verbund-Maßnahmen */}
          {r.missingCombos.length > 0 && (
            <Card className="mb-5" title={<span className="inline-flex items-center gap-1">Fehlende Verbund-Maßnahmen<InfoTip>Personen im Umfang einer Position, die im Zielzustand trotzdem oben bleiben, weil eine andere entfernbare Rolle sie hält (R3) — und diese Rolle hat eine Maßnahme, die nicht im Plan liegt. „Mit aufnehmen" fügt sie mit dem Umfang der ersten betroffenen Position hinzu; ob die Personen dann fallen, zeigt die neue Rechnung.</InfoTip></span>} subtitle={`${formatNum(r.missingCombos.length)} Halter-Maßnahmen außerhalb des Plans · ${formatNum(r.missingCombos.reduce((s, m) => s + m.persons, 0))} Personen-Nennungen`}>
              <MissingTable rows={r.missingCombos.slice(0, MISSING_SHOWN)} items={items} ordered={ordered} />
              {r.missingCombos.length > MISSING_SHOWN && (
                <Aufklapp className="mt-2" label={<>weitere {formatNum(r.missingCombos.length - MISSING_SHOWN)} Halter-Maßnahmen</>}>
                  <MissingTable rows={r.missingCombos.slice(MISSING_SHOWN)} items={items} ordered={ordered} />
                </Aufklapp>
              )}
            </Card>
          )}

          {/* Positionen */}
          <div className="space-y-4">
            {ordered.map(({ item, res }, i) => {
              const lv = leverById.get(item.leverId);
              if (!lv) return null;
              return <PositionCard key={item.leverId} nr={i + 1} item={item} lv={lv} res={res} />;
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Fehlende Verbund-Maßnahmen
const MISSING_SHOWN = 8;
function MissingTable({ rows, items, ordered }: { rows: MissingCombo[]; items: PlanItem[]; ordered: { item: PlanItem }[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-soft"><Th>Rolle</Th><Th>System</Th><Th right>bleiben oben</Th><Th>über Position</Th><Th> </Th></tr></thead>
        <tbody>
          {rows.map((m) => {
            const firstScope = items.find((i) => i.leverId === m.viaItems[0])?.scope ?? "nie";
            return (
              <tr key={m.leverId} className="border-b border-soft last:border-0">
                <td className="py-2 pr-3"><RoleName id={m.roleId} name={m.roleName} /></td>
                <td className="py-2 pr-3 font-mono text-xs">{m.sid}</td>
                <td className="tnum text-strong py-2 pr-3 text-right font-semibold whitespace-nowrap">{formatNum(m.persons)}</td>
                <td className="text-dim py-2 pr-3 text-xs whitespace-nowrap">{m.viaItems.map((v) => { const n = ordered.findIndex((o) => o.item.leverId === v) + 1; return n > 0 ? `Nr. ${n}` : v; }).join(", ")}</td>
                <td className="py-2 text-right"><button type="button" onClick={() => planStore.add(m.leverId, firstScope)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium whitespace-nowrap" style={{ backgroundColor: "var(--color-brand-600)", color: "#fff" }}><Plus size={12} /> mit aufnehmen</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------ Karte je Position
function Kennzahl({ label, value, tone, info }: { label: string; value: ReactNode; tone?: "green" | "red" | "amber"; info?: ReactNode }) {
  const color = tone === "green" ? "var(--color-amp-green)" : tone === "red" ? "var(--color-amp-red)" : tone === "amber" ? "var(--color-amp-amber)" : "var(--text-strong)";
  return (
    <div className="rounded-lg px-3 py-2" style={{ backgroundColor: "var(--surface-sunken)" }}>
      <div className="text-faint inline-flex items-center gap-1 text-[11px] uppercase tracking-wide">{label}{info && <InfoTip size={12}>{info}</InfoTip>}</div>
      <div className="tnum text-lg font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}

function PositionCard({ nr, item, lv, res }: { nr: number; item: PlanItem; lv: Lever; res: PlanItemResult }) {
  const land = landscapeById(lv.landscapeId);
  const total = res.inScope || 1;
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-faint tnum text-xs font-semibold">Nr. {nr}</span>
            <RoleName id={lv.roleId} name={lv.roleName} className="text-base" />
            <Badge tone={EFFORT_TONE[lv.effort]}>Aufwand {lv.effort}</Badge>
            {res.isSplit && <Badge tone="outline">Split</Badge>}
            {item.ticket && <Badge tone="brand"><Ticket size={12} className="mr-1" />Ticket erstellt</Badge>}
          </div>
          <div className="text-dim mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
            <span>System <span className="font-mono text-strong">{lv.sid}</span></span>
            <span>Landschaft <span className="text-strong">{land?.name ?? lv.landscapeId}</span></span>
            <span>{lv.authObject} {lv.field} = {lv.value}</span>
          </div>
          <div className="text-normal mt-1 text-sm">{lv.action}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-dim inline-flex items-center gap-1.5 text-xs">Umfang
            <select value={item.scope} onChange={(e) => planStore.update(item.leverId, { scope: e.target.value as TraceScope })} className="bg-card rounded-md px-2 py-1 text-xs" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-strong)" }}>
              {SCOPES.map((s) => <option key={s} value={s}>{TRACE_SCOPE_LABEL[s]}</option>)}
            </select>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium" style={{ borderWidth: 1, borderColor: item.ticket ? "var(--color-brand-600)" : "var(--border-soft)", color: item.ticket ? "var(--color-brand-600)" : "var(--text-dim)" }}>
            <input type="checkbox" checked={item.ticket} onChange={(e) => planStore.update(item.leverId, { ticket: e.target.checked })} />
            Ticket erstellt
            <InfoTip size={12}>Ticket wird im Hintergrund erstellt; keine Systemanbindung. Das Häkchen dokumentiert nur den Stand.</InfoTip>
          </label>
          <button type="button" onClick={() => planStore.remove(item.leverId)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium" style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--color-amp-red)" }}><Trash2 size={12} /> entfernen</button>
        </div>
      </div>

      {/* Kennzahlen */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Kennzahl label="im Umfang" value={formatNum(res.inScope)} info={<>Träger dieser Rolle, deren Trace zum Umfang passt — von {formatNum(res.carriers)} Trägern insgesamt.</>} />
        <Kennzahl label="fallen" value={formatNum(res.fallen.length)} tone="green" info={<>Personen im Umfang, deren Klasse im Zielzustand sinkt. Davon brauchen {formatNum(res.effective)} diese Position (ohne sie wäre ihr Δ kleiner).</>} />
        <Kennzahl label="bleiben oben" value={formatNum(res.held)} tone={res.held ? "amber" : undefined} info={<>Personen im Umfang, die eine andere Rolle oben hält (R3) — Halter in der Liste unten.</>} />
        <Kennzahl label="Anteil im Plan" value={`−${formatFue1(res.deltaFue)}`} tone="green" info={<>Anteil dieser Position an der Gesamtreduktion: Δ jeder fallenden Person geteilt durch die Zahl der Positionen, die sie dafür braucht. Die Anteile aller Positionen summieren exakt auf den Zielzustand.</>} />
        <Kennzahl label="allein (im Umfang)" value={`−${formatFue1(res.alone)}`} info={<>Wenn NUR diese Position (mit diesem Umfang) umgesetzt würde — sonst nichts. Ohne diese Position verlöre der Plan −{formatFue1(res.marginal)}.</>} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="text-faint uppercase tracking-wide">Übergänge</span>
        {res.transitions.length ? res.transitions.map((t) => (
          <span key={`${t.fromClass}>${t.toClass}`} className="bg-sunken tnum rounded px-2 py-0.5"><span className="text-strong font-semibold">{formatNum(t.persons)}</span> × {CLASS_LABEL[t.fromClass]} → {CLASS_LABEL[t.toClass]} <span className="text-dim">· −{formatFue1(t.fue)}</span></span>
        )) : <span className="text-faint">—</span>}
      </div>

      {/* Trace-Verteilung im Umfang */}
      <div className="mt-3">
        <div className="text-faint mb-1 text-[11px] uppercase tracking-wide">Trace-Verteilung im Umfang (Personen · FUE der Fallenden) · {traceWindowLabel()} · {TRACE_META.sourceShort}</div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
          {TRACES.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: TRACE_COLOR[t] }} />{TRACE_LABEL[t]} <span className="tnum text-dim">{formatNum(res.byTrace[t].persons)} · −{formatFue1(res.byTrace[t].fue)}</span></span>
          ))}
        </div>
        <div className="bg-sunken mt-1 flex h-1.5 w-full overflow-hidden rounded-full">{TRACES.map((t) => <div key={t} style={{ width: `${(res.byTrace[t].persons / total) * 100}%`, backgroundColor: TRACE_COLOR[t] }} />)}</div>
      </div>

      {/* Personenlisten */}
      <div className="mt-3 space-y-2">
        {res.isSplit ? (
          <>
            <Aufklapp label={<>Erhalten die kleinere Rolle (ohne Wert) · {formatNum(res.kleineRolle.length)} Personen</>}><PersonTable rows={res.kleineRolle} /></Aufklapp>
            <Aufklapp label={<>Behalten die größere Rolle (mit Wert) · {formatNum(res.grosseRolle.length)} Personen — außerhalb des Umfangs</>}><PersonTable rows={res.grosseRolle} /></Aufklapp>
          </>
        ) : (
          <Aufklapp label={<>Fallen · {formatNum(res.fallen.length)} Personen</>}><PersonTable rows={res.fallen} /></Aufklapp>
        )}
        {res.bleibenOben.length > 0 && <Aufklapp label={<>Bleiben oben · {formatNum(res.bleibenOben.length)} Personen — wer hält</>}><HeldTable rows={res.bleibenOben} /></Aufklapp>}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------ Tabellen (seitenweise, 60.000 Personen möglich)
function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return <th className={`text-faint py-1.5 pr-3 text-xs font-semibold tracking-wide uppercase whitespace-nowrap ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
function Mehr({ shown, total, onMore }: { shown: number; total: number; onMore: () => void }) {
  if (shown >= total) return null;
  return <div className="text-dim mt-2 flex items-center gap-3 text-xs"><span className="tnum">{formatNum(shown)} von {formatNum(total)}</span><button type="button" onClick={onMore} className="font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>weitere {formatNum(Math.min(PAGE, total - shown))} anzeigen</button><span className="text-faint">vollständige Liste im CSV-Export</span></div>;
}

function PersonTable({ rows }: { rows: PlanPersonRow[] }) {
  const [n, setN] = useState(PAGE);
  if (!rows.length) return <div className="text-faint text-xs">keine</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-soft"><Th>Person</Th><Th>Name</Th><Th>Funktion (SU01)</Th><Th>Abteilung</Th><Th>Klasse vorher → nachher</Th><Th>Trace</Th></tr></thead>
        <tbody>
          {rows.slice(0, n).map((p) => (
            <tr key={p.id} className="border-b border-soft last:border-0">
              <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap"><Link to={`/berechtigungen?person=${p.id}`} className="hover:underline" style={{ color: "var(--color-brand-600)" }}>{p.id}</Link></td>
              <td className="text-strong py-1.5 text-xs">{p.name ?? "—"}</td>
              <td className="text-dim py-1.5 text-xs">{p.funktion ?? "—"}</td>
              <td className="text-dim py-1.5 text-xs">{p.department ?? p.unit}</td>
              <td className="py-1.5 text-xs"><span className="text-strong">{CLASS_LABEL[p.from]}</span> → <span style={{ color: p.delta > 0 ? "var(--color-amp-green)" : "var(--text-dim)" }}>{CLASS_LABEL[p.to]}</span>{p.delta <= 0 && <span className="text-faint"> (bleibt)</span>}</td>
              <td className="py-1.5 text-xs"><span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: TRACE_COLOR[p.trace] }} />{TRACE_LABEL[p.trace]}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Mehr shown={Math.min(n, rows.length)} total={rows.length} onMore={() => setN((x) => x + PAGE)} />
    </div>
  );
}

function HeldTable({ rows }: { rows: PlanHeldRow[] }) {
  const [n, setN] = useState(PAGE);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-soft"><Th>Person</Th><Th>Name</Th><Th>Funktion (SU01)</Th><Th>Abteilung</Th><Th>Klasse</Th><Th>gehalten von</Th><Th>Halter-Maßnahme</Th></tr></thead>
        <tbody>
          {rows.slice(0, n).map((h) => (
            <tr key={h.id} className="border-b border-soft last:border-0">
              <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap"><Link to={`/berechtigungen?person=${h.id}`} className="hover:underline" style={{ color: "var(--color-brand-600)" }}>{h.id}</Link></td>
              <td className="text-strong py-1.5 text-xs">{h.name ?? "—"}</td>
              <td className="text-dim py-1.5 text-xs">{h.funktion ?? "—"}</td>
              <td className="text-dim py-1.5 text-xs">{h.department ?? h.unit}</td>
              <td className="py-1.5 text-xs">{CLASS_LABEL[h.cls]}</td>
              <td className="py-1.5 text-xs"><span className="font-mono">{h.holderSid}</span> <RoleName id={h.holderRoleId} name={h.holderRoleName} className="text-xs" /></td>
              <td className="py-1.5 text-xs">{h.holderMissing ? <Badge tone="amber">fehlt im Plan</Badge> : h.holderOutOfScope ? <Badge tone="gray">im Plan, außerhalb des Umfangs</Badge> : h.holderRoleId === "#DEV#" ? <Badge tone="gray">Entwicklerschlüssel</Badge> : <Badge tone="gray">keine Maßnahme</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Mehr shown={Math.min(n, rows.length)} total={rows.length} onMore={() => setN((x) => x + PAGE)} />
    </div>
  );
}
